/**
 * Image block — renders collapsed by default as a single inline row
 * in the same column as agent narration. Expanding shows a small
 * unframed thumbnail. Clicking the thumbnail opens a pane-scoped
 * preview for closer inspection without leaving the agent chat.
 *
 * The last :data:`AUTO_EXPAND_RECENT_IMAGE_LIMIT` image blocks in
 * the loaded transcript auto-expand on mount via the
 * :data:`AutoExpandImagesContext` provided by ``TurnStream``. This
 * gives the chat a "live, visual" feel for fresh agent screenshots
 * and user-pasted images, without forcing every image in older
 * history pages to download. Users can still click any older image
 * row to expand it on demand.
 *
 * Broken / unresolvable sources fall back to a single dim inline
 * line ``Image unavailable`` rather than a chrome-heavy placeholder.
 */

import { ChevronDown, ChevronRight, ImageIcon } from 'lucide-react';
import * as React from 'react';
import type { ImageBlock, MediaSource } from '@/lib/agent/types';
import { browserMediaUrl } from '@/lib/media-utils/browserMediaUrl';
import { openCodePaneFile } from '@/lib/notebook/codePaneSync';
import { cn } from '@/lib/utils/cn';
import { useAgentImagePreview } from '../AgentImagePreview';

/**
 * How many of the most recent image blocks in the loaded transcript
 * auto-expand on mount. Five matches the user-perceived "what's
 * currently in view / most relevant" window without paying the
 * download cost for older history pages. Tunable -- larger values
 * make the chat feel more visually live but inflate initial bytes
 * on a refresh, smaller values lose the "feels interactive" delight.
 */
export const AUTO_EXPAND_RECENT_IMAGE_LIMIT = 5;

/**
 * Walk turns in reverse and return the at-most-N most-recent image
 * blocks across the whole transcript. Used by ``TurnStream`` to seed
 * the auto-expand context.
 */
export function collectRecentImageBlocks(
  turns: { order: number[]; blocks: Record<number, { block_type: string }> }[],
  limit: number = AUTO_EXPAND_RECENT_IMAGE_LIMIT
): Set<ImageBlock> {
  const recent: ImageBlock[] = [];
  outer: for (let ti = turns.length - 1; ti >= 0; ti--) {
    const turn = turns[ti];
    for (let bi = turn.order.length - 1; bi >= 0; bi--) {
      const block = turn.blocks[turn.order[bi]];
      if (block?.block_type === 'ImageBlock') {
        recent.push(block as ImageBlock);
        if (recent.length >= limit) break outer;
      }
    }
  }
  return new Set(recent);
}

/**
 * Set of ``ImageBlock`` references that should mount in the expanded
 * state. ``TurnStream`` provides this via
 * :data:`AutoExpandImagesProvider`; ``ImageBlockView`` consumes it
 * in its initial-state computation only (re-renders never collapse
 * a block the user has manually expanded).
 *
 * Identity-based on purpose: the reducer's immutable update model
 * means equivalent image blocks share object identity, and using
 * the block reference as the key avoids serializing big base64 src
 * strings into the set.
 */
const AutoExpandImagesContext = React.createContext<Set<ImageBlock>>(new Set());

export interface AutoExpandImagesProviderProps {
  /** The set of ``ImageBlock`` references to auto-expand on mount. */
  value: Set<ImageBlock>;
  children: React.ReactNode;
}

/** Provider for the auto-expand-recent-images set. See ``TurnStream``. */
export function AutoExpandImagesProvider({ value, children }: AutoExpandImagesProviderProps): React.ReactElement {
  return <AutoExpandImagesContext.Provider value={value}>{children}</AutoExpandImagesContext.Provider>;
}

export interface ImageBlockViewProps {
  block: ImageBlock;
  /** Compact variant used inside tool-result arrays — trims the outer margin. */
  dense?: boolean;
  /** Render the thumbnail directly when an outer tool row already owns expansion. */
  inlinePreview?: boolean;
  /** Workspace file candidates to prefer over cached media URLs when opening Code Pane. */
  preferredPathCandidates?: string[];
  className?: string;
}

export function ImageBlockView({
  block,
  dense = false,
  inlinePreview = false,
  preferredPathCandidates = [],
  className,
}: ImageBlockViewProps) {
  const imagePreview = useAgentImagePreview();
  // Initial expand state comes from the auto-expand context: the
  // last N image blocks in the loaded transcript open by default
  // so the chat reads as "live and visual" without forcing older
  // history pages to download. The initializer runs once at mount;
  // later context changes (new images stream in, the recent window
  // shifts) do NOT collapse images the user has already
  // expanded -- the user always wins.
  const autoExpandSet = React.useContext(AutoExpandImagesContext);
  const [expanded, setExpanded] = React.useState<boolean>(() => autoExpandSet.has(block));
  const [broken, setBroken] = React.useState(false);
  const shouldResolve = inlinePreview || expanded;
  const url = shouldResolve ? mediaSrc(block.source) : null;
  const previewUrl = shouldResolve ? previewSrc(block.source) : null;
  const mediaType = mediaTypeOf(block.source);
  const previewMediaType = previewMediaTypeOf(block.source);
  const unavailableReason = block.source.type === 'omitted' ? block.source.reason : null;
  const alt = block.caption ?? 'attachment';
  const previewLabel = block.caption ?? mediaType ?? alt;
  const downloadName = imageDownloadName(block.caption, previewMediaType);
  const canPreview = Boolean(url && previewUrl && imagePreview);
  const openPreview = React.useCallback(() => {
    const preferredPath = preferredPathCandidates[0];
    if (preferredPath && openCodePaneFile(preferredPath, 'agent-image-preview', preferredPathCandidates)) return;
    if (!previewUrl || !imagePreview) return;
    imagePreview.openImage({ src: previewUrl, alt, caption: previewLabel, mediaType: previewMediaType, downloadName });
  }, [alt, downloadName, imagePreview, preferredPathCandidates, previewLabel, previewMediaType, previewUrl]);
  React.useEffect(() => {
    if (url) {
      setBroken(false);
    }
  }, [url]);

  if (inlinePreview && (!url || broken)) {
    return (
      <div className={cn('inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400', className)}>
        <ImageIcon className="size-3.5" aria-hidden />
        <span>
          Image unavailable{mediaType ? ` (${mediaType})` : ''}
          {unavailableReason ? ` · ${unavailableReason}` : ''}
        </span>
      </div>
    );
  }

  if (inlinePreview) {
    const resolvedUrl = url;
    return (
      <div className={cn('text-[14px]', dense ? 'my-0' : 'my-0.5', className)}>
        {canPreview ? (
          <button
            type="button"
            onClick={openPreview}
            aria-label={`Preview ${previewLabel}`}
            className="block max-w-full cursor-zoom-in rounded-sm text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-400"
          >
            <img
              src={resolvedUrl ?? ''}
              alt={alt}
              onError={() => setBroken(true)}
              className="block max-h-72 max-w-full rounded-sm object-contain"
            />
          </button>
        ) : (
          <img
            src={resolvedUrl ?? ''}
            alt={alt}
            onError={() => setBroken(true)}
            className="block max-h-72 max-w-full rounded-sm object-contain"
          />
        )}
      </div>
    );
  }

  return (
    <div className={cn('group/image text-[14px]', dense ? 'my-0' : 'my-0.5', className)}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={cn(
          '-mx-1 inline-flex max-w-full cursor-pointer items-baseline gap-1 rounded px-1 py-px text-left text-gray-600 transition-colors hover:bg-gray-100/70 dark:text-gray-300 dark:hover:bg-white/[0.04]'
        )}
      >
        <span className="min-w-0 truncate">
          <span className="font-medium text-gray-700 dark:text-gray-200">Image</span>
          {block.caption && (
            <>
              {' '}
              <span className="text-gray-400 dark:text-gray-500">{block.caption}</span>
            </>
          )}
          {!block.caption && mediaType && (
            <>
              {' '}
              <span className="text-gray-400 dark:text-gray-500">{mediaType}</span>
            </>
          )}
        </span>
        <span
          className={cn(
            'shrink-0 self-center text-gray-400 transition-opacity dark:text-gray-500',
            expanded ? 'opacity-100' : 'opacity-70 group-hover/image:opacity-100'
          )}
          aria-hidden
        >
          {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        </span>
      </button>
      <div
        aria-hidden={!expanded}
        className={cn(
          'grid transition-all duration-200 ease-out',
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          {!expanded ? null : !url || broken ? (
            <div className="mt-1 inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
              <ImageIcon className="size-3.5" aria-hidden />
              <span>
                Image unavailable{mediaType ? ` (${mediaType})` : ''}
                {unavailableReason ? ` · ${unavailableReason}` : ''}
              </span>
            </div>
          ) : canPreview ? (
            <button
              type="button"
              onClick={openPreview}
              aria-label={`Preview ${previewLabel}`}
              className="mt-1 block max-w-full cursor-zoom-in rounded-sm text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-400"
            >
              <img
                src={url}
                alt={alt}
                onError={() => setBroken(true)}
                className="block max-h-72 max-w-full rounded-sm object-contain"
              />
            </button>
          ) : (
            <img
              src={url}
              alt={alt}
              onError={() => setBroken(true)}
              className="mt-1 block max-h-72 max-w-full rounded-sm object-contain"
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** Resolve a MediaSource to a browser-loadable URL. */
function mediaSrc(source: MediaSource): string | null {
  if (source.type === 'base64') {
    if (!source.data) return null;
    return `data:${source.media_type};base64,${source.data}`;
  }
  if (source.type === 'url') return browserMediaUrl(source.url);
  // ``file`` sources point at an opaque file_id we expect to resolve
  // through a Rome-backed endpoint. Until that proxy lands, the block
  // falls through to the placeholder.
  return null;
}

function previewSrc(source: MediaSource): string | null {
  if (source.type === 'url') return browserMediaUrl(source.original_url ?? source.url);
  return mediaSrc(source);
}

function imageDownloadName(caption: string | null | undefined, mediaType: string | undefined): string {
  if (caption) {
    const name = caption.split('/').filter(Boolean).pop();
    if (name) return name;
  }
  const ext = mediaType?.split('/')[1] ?? 'png';
  return `image.${ext === 'jpeg' ? 'jpg' : ext}`;
}

function mediaTypeOf(source: MediaSource): string | undefined {
  if (source.type === 'base64') return source.media_type;
  if (source.type === 'url') return source.media_type ?? source.original_media_type;
  if (source.type === 'file') return source.media_type;
  if (source.type === 'omitted') return source.media_type;
  return undefined;
}

function previewMediaTypeOf(source: MediaSource): string | undefined {
  if (source.type === 'url') return source.original_media_type ?? source.media_type;
  return mediaTypeOf(source);
}
